import { parseCode } from "@/lib/episode";
import type { QueueEntry } from "@/lib/episode";
import { splitQueue } from "@/lib/stale";
import type { OutboxAction } from "@/store/outbox";

import { QueueRow } from "./QueueRow";

interface Props {
  queue: readonly QueueEntry[];
  loading: boolean;
  /** Shows the user has set aside; they sink to their own section. */
  asideIds: ReadonlySet<number>;
  onAction: (action: OutboxAction) => void;
  onOpen: (entry: QueueEntry) => void;
}

function Skeleton() {
  return (
    <ul className="rows">
      {[0, 1, 2, 3, 4, 5].map((n) => (
        <li key={n} className="skeleton">
          <span className="skeleton-poster" />
          <span className="skeleton-lines">
            <span className="skeleton-line" />
            <span className="skeleton-line short" />
          </span>
        </li>
      ))}
    </ul>
  );
}

function Rows({
  entries,
  onAction,
  onOpen,
}: {
  entries: readonly QueueEntry[];
  onAction: (action: OutboxAction) => void;
  onOpen: (entry: QueueEntry) => void;
}) {
  return (
    <ul className="rows">
      {entries.map((entry) => (
        <QueueRow
          key={entry.show.show.ids.simkl}
          entry={entry}
          onMarkWatched={() => {
            const ref = parseCode(entry.code);
            if (ref)
              onAction({
                kind: "watch",
                target: {
                  simklId: entry.show.show.ids.simkl,
                  season: ref.season,
                  episode: ref.episode,
                },
              });
          }}
          onMarkPrevious={() => {
            const ref = parseCode(entry.show.lastWatched);
            if (ref)
              onAction({
                kind: "unwatch",
                target: {
                  simklId: entry.show.show.ids.simkl,
                  season: ref.season,
                  episode: ref.episode,
                },
              });
          }}
          onOpen={() => {
            onOpen(entry);
          }}
        />
      ))}
    </ul>
  );
}

export function UpNext({ queue, loading, asideIds, onAction, onOpen }: Props) {
  if (loading && queue.length === 0) return <Skeleton />;

  if (queue.length === 0) {
    return (
      <div className="empty">
        <strong>All caught up</strong>
        <p>Nothing waiting. New episodes show up here as they air.</p>
      </div>
    );
  }

  const { active, stale } = splitQueue(queue, asideIds);

  return (
    <>
      {active.length === 0 ? (
        <div className="empty">
          <strong>Nothing on the go</strong>
          <p>Everything you are part-way through has been set aside.</p>
        </div>
      ) : (
        <Rows entries={active} onAction={onAction} onOpen={onOpen} />
      )}

      {stale.length > 0 && (
        <section>
          <h2 className="section-head">Haven&rsquo;t watched in a while</h2>
          <Rows entries={stale} onAction={onAction} onOpen={onOpen} />
        </section>
      )}
    </>
  );
}
